import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { X, BedDouble, FlaskConical, ScanLine, Pill, Briefcase, LogOut, ClipboardList } from 'lucide-react';
import { toast } from './notify';
import { ItemsTable, money, formatVitals, VITAL_FIELDS, deptLabel } from './VisitBits';
import PatientHistory from './PatientHistory';
import { formatCATDateTime } from '../../utils/timezone';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BUCKETS = [
  { key: 'lab', label: 'Lab', title: 'Laboratory Tests', icon: FlaskConical, cls: 'bg-purple-600 hover:bg-purple-700' },
  { key: 'rad', label: 'Rad', title: 'Radiology / Imaging', icon: ScanLine, cls: 'bg-sky-600 hover:bg-sky-700' },
  { key: 'rx', label: 'Rx', title: 'Medications', icon: Pill, cls: 'bg-emerald-600 hover:bg-emerald-700' },
  { key: 'service', label: 'Service', title: 'Services / Fees (e.g. bed day, nursing)', icon: Briefcase, cls: 'bg-gray-600 hover:bg-gray-700' },
];

// Ward rounds + discharge for an admitted (inpatient) visit.
export default function InpatientPanel({ visit: initial, authCfg, isSuperAdmin, onClose, refresh }) {
  const [visit, setVisit] = useState(initial);
  const [priceList, setPriceList] = useState([]);
  const [search, setSearch] = useState('');
  const [notes, setNotes] = useState('');
  const [vitals, setVitals] = useState({});
  const [orders, setOrders] = useState({ lab: [], rad: [], dental: [], rx: [], service: [] });
  const [discharging, setDischarging] = useState(false);
  const [summary, setSummary] = useState('');
  const [finalDx, setFinalDx] = useState(visit.consultation?.final_diagnosis || '');
  const [followUp, setFollowUp] = useState({ date: '', notes: '', condition: 'Improved' });
  const [busy, setBusy] = useState(false);

  const reload = async () => {
    try { const r = await axios.get(`${API}/visits/${visit.visit_id}`, authCfg); setVisit(r.data); } catch (e) { /* keep */ }
    refresh();
  };

  useEffect(() => {
    const url = isSuperAdmin ? `${API}/admin/hospitals/${encodeURIComponent(visit.hospital_name)}/pricelist` : `${API}/pricelists`;
    axios.get(url, authCfg).then((r) => setPriceList(isSuperAdmin ? (r.data.items || []) : (r.data || []))).catch(() => toast.error('Could not load the hospital price list'));
    // eslint-disable-next-line
  }, [visit.hospital_name]);

  const matches = search.trim().length < 2 ? [] : priceList.filter((i) => i.item_name.toLowerCase().includes(search.toLowerCase()) || i.item_id.toLowerCase().includes(search.toLowerCase())).slice(0, 8);
  const add = (key, item) => setOrders((p) => ({ ...p, [key]: [...p[key], { item_id: item.item_id, item_name: item.item_name, cost: item.cost, quantity: 1, instructions: '' }] }));
  const update = (key, idx, patch) => setOrders((p) => ({ ...p, [key]: p[key].map((o, i) => (i === idx ? { ...o, ...patch } : o)) }));
  const drop = (key, idx) => setOrders((p) => ({ ...p, [key]: p[key].filter((_, i) => i !== idx) }));
  const totalNew = Object.values(orders).reduce((n, arr) => n + arr.length, 0);

  const submitRound = async () => {
    if (!notes.trim() && totalNew === 0) { toast.error('Enter a progress note or order at least one item'); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/visits/${visit.visit_id}/admission/round`, {
        notes, vitals: Object.fromEntries(Object.entries(vitals).filter(([, v]) => v && v.trim())),
        lab_orders: orders.lab, rad_orders: orders.rad, dental_orders: orders.dental, prescriptions: orders.rx, service_items: orders.service,
      }, authCfg);
      toast.success(`Ward round saved${totalNew ? ` — ${totalNew} item(s) sent to the departments` : ''}`);
      setNotes(''); setVitals({}); setOrders({ lab: [], rad: [], dental: [], rx: [], service: [] });
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save ward round');
    } finally { setBusy(false); }
  };

  const submitDischarge = async () => {
    if (!summary.trim() || !finalDx.trim()) { toast.error('Discharge summary and final diagnosis are required'); return; }
    setBusy(true);
    try {
      const r = await axios.post(`${API}/visits/${visit.visit_id}/admission/discharge`, { discharge_summary: summary, final_diagnosis: finalDx, prescriptions: orders.rx, follow_up_date: followUp.date || null, follow_up_notes: followUp.notes, condition_at_discharge: followUp.condition }, authCfg);
      toast.success(`Patient discharged${r.data.bed_days ? ` after ${r.data.bed_days} bed day(s)` : ''} — ${r.data.status === 'PHARMACY_PENDING' ? 'sent to pharmacy for take-home medicines, then billing' : 'ready for billing'}. Opening the discharge letter…`, { duration: 8000 });
      window.open(`/print-discharge/${visit.visit_id}`, '_blank');
      refresh(); onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to discharge');
    } finally { setBusy(false); }
  };

  const adm = visit.admission || {};
  const pendingTests = ['lab_orders', 'rad_orders', 'dental_orders'].flatMap((k) => (visit[k] || []).filter((o) => o.status === 'pending'));
  const done = (k) => (visit[k] || []).filter((o) => o.status === 'completed');

  return (
    <Card className="border-rose-200 shadow-md" data-testid="inpatient-panel">
      <CardHeader className="bg-gradient-to-r from-rose-50 to-pink-50 border-b border-rose-200 p-4">
        <CardTitle className="flex items-center justify-between text-rose-900 text-base gap-2">
          <span className="flex items-center gap-2 min-w-0"><BedDouble className="w-5 h-5" /> Inpatient — {visit.patient_name} <span className="text-xs font-mono font-normal text-rose-700">{adm.number}</span></span>
          <Button variant="outline" size="sm" onClick={onClose} data-testid="inpatient-close"><X className="w-4 h-4 mr-1" /> Back</Button>
        </CardTitle>
      </CardHeader>
      <CardContent className="p-4 space-y-4">
        <div className="text-sm text-gray-700 bg-gray-50 rounded-lg p-3 space-y-1" data-testid="inpatient-info">
          <p>{visit.visit_id} • Serial: {visit.patient_serial_number} • {visit.patient_sex} • DOB: {visit.patient_dob}</p>
          <p><span className="font-medium">Admitted:</span> {formatCATDateTime(adm.approved_at)} • <span className="font-medium">Ward:</span> {adm.ward || '—'} • <span className="font-medium">Reason:</span> {adm.reason}{adm.bed_item ? <span> • <span className="font-medium">Bed charge:</span> {adm.bed_item.item_name} {money(visit, adm.bed_item.cost)}/day</span> : null}</p>
          {visit.consultation?.complaints && <p className="whitespace-pre-line"><span className="font-medium">Complaints:</span> {visit.consultation.complaints}</p>}
          {visit.consultation?.provisional_diagnosis && <p className="whitespace-pre-line"><span className="font-medium">Provisional Dx:</span> {visit.consultation.provisional_diagnosis}</p>}
        </div>

        <PatientHistory serial={visit.patient_serial_number} authCfg={authCfg} currentVisitId={visit.visit_id} />

        <ItemsTable title="Lab Results" items={done('lab_orders')} visit={visit} showResult />
        <ItemsTable title="Radiology Results" items={done('rad_orders')} visit={visit} showResult />
        <ItemsTable title="Dental Results" items={done('dental_orders')} visit={visit} showResult />
        {pendingTests.length > 0 && <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded p-2" data-testid="inpatient-pending-tests">Waiting for results: {pendingTests.map((o) => o.item_name).join(', ')}</p>}
        <ItemsTable title="Medications (whole stay)" items={(visit.prescriptions || []).filter((p) => p.status !== 'removed')} visit={visit} showInstructions />
        <ItemsTable title="Services / Fees" items={visit.service_items || []} visit={visit} />

        {(visit.rounds || []).length > 0 && (
          <div className="border border-rose-100 rounded-lg" data-testid="inpatient-rounds">
            <p className="px-3 py-2 text-sm font-bold text-rose-800 flex items-center gap-1 bg-rose-50/60 rounded-t-lg"><ClipboardList className="w-4 h-4" /> Ward rounds ({visit.rounds.length})</p>
            <ul className="divide-y text-sm">
              {[...visit.rounds].reverse().map((r) => (
                <li key={r.round_id} className="px-3 py-2">
                  <p className="text-xs text-gray-500">{formatCATDateTime(r.at)} • {r.by}{formatVitals(r.vitals) ? ` • ${formatVitals(r.vitals)}` : ''}</p>
                  <p className="whitespace-pre-line text-gray-800">{r.notes || <span className="text-gray-400">(orders only)</span>}</p>
                </li>
              ))}
            </ul>
          </div>
        )}

        {!discharging ? (
          <>
            <div className="border border-rose-200 rounded-xl p-3 bg-rose-50/30 space-y-3" data-testid="inpatient-round-form">
              <p className="text-sm font-bold text-gray-800">New ward round</p>
              <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} placeholder="Progress notes — condition, examination, plan…" className="w-full rounded-md border border-gray-300 p-2 text-sm bg-white focus:border-rose-500 focus:outline-none" data-testid="round-notes" />
              <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
                {VITAL_FIELDS.map((f) => <Input key={f.key} value={vitals[f.key] || ''} onChange={(e) => setVitals((p) => ({ ...p, [f.key]: e.target.value }))} placeholder={f.label} className="h-8 text-xs bg-white" data-testid={`round-vitals-${f.key}`} />)}
              </div>
              <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search price list to order tests, medicines or services…" className="bg-white" data-testid="round-item-search" />
              {matches.length > 0 && (
                <div className="bg-white border border-gray-200 rounded-lg divide-y max-h-64 overflow-y-auto">
                  {matches.map((i) => (
                    <div key={i.item_id} className="px-3 py-2 text-sm flex items-center justify-between gap-2 flex-wrap">
                      <span className="min-w-0 truncate">{i.item_name} <span className="text-xs text-gray-400">{i.item_id}{i.uom ? ` • ${i.uom}` : ''} • {money(visit, i.cost)}</span></span>
                      <span className="flex gap-1">
                        {BUCKETS.map((b) => <button key={b.key} onClick={() => add(b.key, i)} className={`${b.cls} text-white text-[11px] px-2 py-0.5 rounded`} data-testid={`round-add-${b.key}-${i.item_id}`}>{b.label}</button>)}
                      </span>
                    </div>
                  ))}
                </div>
              )}
              {BUCKETS.filter((b) => orders[b.key].length > 0).map((b) => (
                <div key={b.key} data-testid={`round-bucket-${b.key}`}>
                  <p className="text-xs font-semibold text-gray-600 mb-1">{b.title}</p>
                  {orders[b.key].map((o, idx) => (
                    <div key={idx} className="flex items-center gap-2 text-sm py-1">
                      <span className="flex-1 truncate">{o.item_name}</span>
                      <Input type="number" min={1} value={o.quantity} onChange={(e) => update(b.key, idx, { quantity: Number(e.target.value) || 1 })} className="w-16 h-7 text-xs bg-white" />
                      {b.key === 'rx' && <Input value={o.instructions} onChange={(e) => update(b.key, idx, { instructions: e.target.value })} placeholder="Dosage" className="w-40 h-7 text-xs bg-white" />}
                      <button onClick={() => drop(b.key, idx)} className="text-red-500 text-xs" data-testid={`round-drop-${b.key}-${idx}`}><X className="w-4 h-4" /></button>
                    </div>
                  ))}
                </div>
              ))}
              <Button onClick={submitRound} disabled={busy} className="w-full bg-rose-600 hover:bg-rose-700" data-testid="round-submit">{busy ? 'Saving…' : `Save Ward Round${totalNew ? ` & Send ${totalNew} Order(s)` : ''}`}</Button>
            </div>
            <Button variant="outline" onClick={() => setDischarging(true)} className="w-full border-emerald-300 text-emerald-700 hover:bg-emerald-50" data-testid="discharge-start"><LogOut className="w-4 h-4 mr-1" /> Discharge Patient</Button>
          </>
        ) : (
          <div className="border border-emerald-300 rounded-xl p-3 bg-emerald-50/40 space-y-3" data-testid="discharge-form">
            <p className="text-sm font-bold text-emerald-900">Discharge — after this, reception can bill the whole stay{adm.bed_item ? ' (bed days are added automatically)' : ''}</p>
            <div>
              <label className="text-xs text-gray-600">Final diagnosis <span className="text-red-500">*</span></label>
              <textarea value={finalDx} onChange={(e) => setFinalDx(e.target.value)} rows={2} className="w-full rounded-md border border-gray-300 p-2 text-sm bg-white" data-testid="discharge-final-dx" />
            </div>
            <div>
              <label className="text-xs text-gray-600">Discharge summary <span className="text-red-500">*</span></label>
              <textarea value={summary} onChange={(e) => setSummary(e.target.value)} rows={4} placeholder="Course in hospital, condition at discharge, follow-up instructions…" className="w-full rounded-md border border-gray-300 p-2 text-sm bg-white" data-testid="discharge-summary" />
            </div>
            <div className="grid sm:grid-cols-3 gap-2">
              <div>
                <label className="text-xs text-gray-600">Follow-up date</label>
                <Input type="date" value={followUp.date} onChange={(e) => setFollowUp((p) => ({ ...p, date: e.target.value }))} className="bg-white h-9" data-testid="discharge-followup-date" />
              </div>
              <div>
                <label className="text-xs text-gray-600">Condition at discharge</label>
                <select value={followUp.condition} onChange={(e) => setFollowUp((p) => ({ ...p, condition: e.target.value }))} className="w-full h-9 rounded-md border border-gray-300 px-2 text-sm bg-white" data-testid="discharge-condition">
                  {['Improved', 'Stable', 'Recovered', 'Referred', 'Discharged against advice'].map((o) => <option key={o}>{o}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs text-gray-600">Follow-up instructions</label>
                <Input value={followUp.notes} onChange={(e) => setFollowUp((p) => ({ ...p, notes: e.target.value }))} placeholder="e.g. Review with repeat FBC" className="bg-white h-9" data-testid="discharge-followup-notes" />
              </div>
            </div>
            <p className="text-xs text-gray-600">{orders.rx.length > 0 ? `${orders.rx.length} take-home medicine(s) from the round form will be sent to pharmacy and listed on the letter. ` : 'Tip: add take-home medicines in the round form above (Rx) before discharging. '}A printable discharge letter opens after confirming.</p>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setDischarging(false)} data-testid="discharge-cancel">Back</Button>
              <Button onClick={submitDischarge} disabled={busy} className="flex-1 bg-emerald-600 hover:bg-emerald-700" data-testid="discharge-submit">{busy ? 'Discharging…' : 'Confirm Discharge'}</Button>
            </div>
          </div>
        )}
        <p className="text-[11px] text-gray-400">{deptLabel(adm.department)} inpatient • requested by {adm.requested_by}</p>
      </CardContent>
    </Card>
  );
}
